import { NgClass, registerLocaleData } from '@angular/common';
import localeEnGb from '@angular/common/locales/en-GB';
import {
    AfterViewInit,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    Injector,
    OnInit,
    signal,
    Signal,
    WritableSignal,
    CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { mapCountryCodeToLanguageCode } from '@shs-digi-care-monorepo/interfaces';
import { filter, map, tap } from 'rxjs';
import { CLW_MENU_ROUTE } from './clw/app.routes';
import { NAM_MENU_ROUTE } from './common/app.routes';
import { ActionRequiredComponent } from './common/shared/components/action-required/action-required.component';
import { BannerComponent } from './common/shared/components/banner/banner.component';
import { FooterComponent } from './common/shared/components/footer/footer.component';
import { HeaderComponent } from './common/shared/components/header/header.component';
import { IUser } from './common/shared/interfaces/iUser';
import { SetScope } from './common/shared/states/scope/scope.actions';
import { ScopeState } from './common/shared/states/scope/scope.state';
import { MDLA_SCOPE_ENUM } from './common/shared/tokens/mdla-scope.token';
import type { UserInfoComponentResult } from './common/shared/types/dialog-results';
import { ApplicationState } from './common/store/application/settings.state';
import { FeatureFlagState } from './common/store/feature-flag';
import { UserSettings } from './common/store/settings/settings.actions';
import { Users, UsersState } from './common/store/user';
import { EMEA_MENU_ROUTE } from './emea/app.routes';

const DEFAULT_LANGUAGE = 'en';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [BannerComponent, NgClass, RouterOutlet, FooterComponent, HeaderComponent, ActionRequiredComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit, AfterViewInit {
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly router: Router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly store = inject(Store);
    protected readonly injector = inject(Injector);

    protected readonly mdlaScope: Signal<MDLA_SCOPE_ENUM> = select(ScopeState.scope);
    protected readonly me: Signal<IUser | undefined> = this.store.selectSignal(UsersState.getMe());
    protected readonly userProfileLoading: Signal<boolean> = this.store.selectSignal(UsersState.userProfileLoading());
    protected readonly MDLA_SCOPE_ENUM = MDLA_SCOPE_ENUM;

    protected newLandingPageDesign: Signal<boolean> = select(FeatureFlagState.newLandingPageDesign);
    protected isMenu: WritableSignal<boolean> = signal<boolean>(false);

    protected readonly showEnvironmentHint: Signal<boolean> = this.store.selectSignal(
        ApplicationState.getShowEnvironmentHint
    );

    public readonly isNewDesign = computed(() => {
        return this.mdlaScope() === MDLA_SCOPE_ENUM.EMEA || this.mdlaScope() === MDLA_SCOPE_ENUM.CLW;
    });

    protected readonly showActionRequired: Signal<boolean> = computed(() => {
        return this.mdlaScope() === MDLA_SCOPE_ENUM.NAM;
    });

    private isAdminRoute: WritableSignal<boolean> = signal(false);

    public ngOnInit(): void {
        this.loadUserSettings();
        this.initIsMenu();
        this.initScopeFromUrl();
        this.initLastMenuVisited();
        this.setDefaultLanguage();
        this.listenToLanguageChanges();
        this.listenToRouteChanges();
        this.registerLocales();
    }

    public ngAfterViewInit(): void {
        this.displayIncompleteProfileInfoPopup();
    }

    private setDefaultLanguage(): void {
        this.translate.setDefaultLang(DEFAULT_LANGUAGE);

        const storedLang = localStorage.getItem('language');
        if (storedLang) {
            this.translate.use(storedLang);
        } else {
            this.translate.setDefaultLang(DEFAULT_LANGUAGE);
            this.applyUserLanguageWhenAvailable();
        }
    }

    private applyUserLanguageWhenAvailable(): void {
        effect(
            () => {
                // React to changes in the user data, which may arrive asynchronously after component initialization
                const user = this.me();
                // User object might be undefined at the beginning, so we need to react to changes and not just read it once
                if (user?.country) {
                    const userLanguage = mapCountryCodeToLanguageCode(user.country);
                    // We only want to apply the user language if there's no stored language in localStorage, to avoid overriding the user's explicit choice in a previous session
                    if (!localStorage.getItem('language')) {
                        this.translate.use(userLanguage);
                    }
                }
            },
            { injector: this.injector }
        );
    }

    private listenToLanguageChanges(): void {
        this.translate.onLangChange
            .pipe(
                tap((event) => localStorage.setItem('language', event.lang)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private registerLocales(): void {
        registerLocaleData(localeEnGb);
    }

    private listenToRouteChanges(): void {
        // Check the url to determine if the new design should be applied
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map((event) => event.url),
                tap((url) => {
                    this.setScopeBasedOnUrl(url);
                    this.isMenu.set(this.isMenuUrl(url));
                    this.updateLastMenuVisited(url);
                })
            )
            .subscribe();
    }

    private initScopeFromUrl(): void {
        const currentUrl = this.router.url;

        this.isAdminRoute.set(currentUrl.includes('admin'));

        if (currentUrl) {
            this.setScopeBasedOnUrl(currentUrl);
        } else {
            console.warn('No URL found to determine scope.');
        }
    }

    private initIsMenu(): void {
        const currentUrl = this.router.url;
        this.isMenu.set(this.isMenuUrl(currentUrl));
    }

    private setScopeBasedOnUrl(url: string): void {
        const scope = this.mapUrlToScope(url);
        this.store.dispatch(new SetScope({ scope }));
    }

    private mapUrlToScope(url: string): MDLA_SCOPE_ENUM {
        if (url.includes('emea')) {
            return MDLA_SCOPE_ENUM.EMEA;
        } else if (url.includes('clw')) {
            return MDLA_SCOPE_ENUM.CLW;
        } else {
            return MDLA_SCOPE_ENUM.NAM;
        }
    }

    private displayIncompleteProfileInfoPopup(): void {
        effect(
            async () => {
                const user = this.me();
                const scopeNam = this.mdlaScope() === MDLA_SCOPE_ENUM.NAM; // Only show for NAM scope
                const profileSetupLater = sessionStorage.getItem('profileSetupLater');
                if (
                    user &&
                    !user.userProfileComplete &&
                    profileSetupLater !== 'true' &&
                    scopeNam &&
                    !this.isAdminRoute()
                ) {
                    // Dynamically import MatDialog and UserInfoComponent to reduce initial bundle size
                    const [{ MatDialog }, { UserInfoComponent }] = await Promise.all([
                        import('@angular/material/dialog'),
                        import('./common/users/dialogs/user-info/user-info.component')
                    ]);
                    const dialog = this.injector.get(MatDialog);
                    dialog
                        .open(UserInfoComponent, {
                            data: user
                        })
                        .afterClosed()
                        .pipe(
                            tap((result: UserInfoComponentResult | undefined) => {
                                if (result) {
                                    this.store.dispatch(new Users.patchMe({ profile: result }));
                                } else {
                                    // User closed the dialog without completing info
                                    sessionStorage.setItem('profileSetupLater', 'true');
                                }
                            })
                        )
                        .subscribe();
                }
            },
            { injector: this.injector }
        );
    }

    private loadUserSettings(): void {
        this.store.dispatch(new UserSettings.load());
    }

    private isMenuUrl(url: string): boolean {
        return url.includes('menu');
    }

    private initLastMenuVisited(): void {
        const url = this.router.url;
        this.updateLastMenuVisited(url);
    }

    private updateLastMenuVisited(url: string): void {
        if (url.includes('admin')) {
            return;
        }

        if (url.includes('menu')) {
            localStorage.setItem(
                'lastMenuVisited',
                JSON.stringify({
                    route: url
                })
            );
            return;
        }

        let menuRoute: string | null = null;

        if (url.startsWith('/emea/')) {
            menuRoute = EMEA_MENU_ROUTE;
        } else if (url.startsWith('/clw/')) {
            menuRoute = CLW_MENU_ROUTE;
        } else {
            menuRoute = NAM_MENU_ROUTE;
        }

        if (menuRoute) {
            localStorage.setItem(
                'lastMenuVisited',
                JSON.stringify({
                    route: menuRoute
                })
            );
        }
    }
}
